import React, { useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { LEARN } from "../../theme";
import { useBreakpoint } from "../../shell/LearnShell";
import { ConfettiBurst } from "../../feedback/Celebration";
import { fitTiles, itemKey, seededShuffle, useCompleteOnce } from "./engineUtils";
import { Counter, IconSpeaker, ROOT_STYLE, RoundButton, SparkleBurst, StickerTile, frost, useArenaMetrics, useLetterAccent, useTileFeedback } from "./tapUtils";

/**
 * "Find {word}": the word is spoken and shown; tap its sticker in a 2×2
 * grid. Completes after `roundSize` (default 5) correct rounds.
 */
export default function PictureGridGame({ letter, config = {}, speak, replay, sfx, onComplete, onWrong, reduceMotion }) {
  const finish = useCompleteOnce(onComplete);
  const { accent, deep } = useLetterAccent(letter);
  const bp = useBreakpoint();
  const compact = bp === "xs";
  const rootRef = useRef(null);
  const headRef = useRef(null);
  const { w, h, tile } = useArenaMetrics(rootRef);
  const head = useArenaMetrics(headRef);
  const bank = useMemo(() => seededShuffle(config.items || config.targetItems || [], 21), [config.items, config.targetItems]);
  const rounds = Math.min(config.roundSize || 5, bank.length || 5);
  const [ri, setRi] = useState(0);
  const [solved, setSolved] = useState(false);
  const { shakes, pulses, shakeId, pulseId } = useTileFeedback();
  const target = bank[ri % Math.max(bank.length, 1)];
  const tpl = config.spokenPromptTemplate || "Find {word}";
  const line = target ? tpl.replace("{word}", target.word) : "";

  const choices = useMemo(() => {
    if (!target) return [];
    const others = seededShuffle(bank.filter((x) => x.word !== target.word), 100 + ri).slice(0, 3);
    return seededShuffle([target, ...others], 200 + ri);
  }, [bank, target, ri]);

  // The round's target word is the content of this game (not the stage prompt).
  useEffect(() => {
    if (line) speak?.(line);
  }, [line, ri, speak]);

  useEffect(() => {
    if (!solved) return undefined;
    const t = setTimeout(() => {
      if (ri + 1 >= rounds) { finish(); return; }
      setRi((x) => x + 1);
      setSolved(false);
    }, reduceMotion ? 300 : 950);
    return () => clearTimeout(t);
  }, [solved, ri, rounds, finish, reduceMotion]);

  if (!target) return <div style={ROOT_STYLE} />;

  const tap = (it) => {
    if (solved) return;
    const key = itemKey(it);
    if (it.word !== target.word) { shakeId(key); onWrong?.(); return; }
    sfx?.("correct");
    speak?.(it.word);
    pulseId(key);
    setSolved(true);
  };

  const finalRound = solved && ri + 1 >= rounds;
  // A handful of choices should fill the arena: 2×2 up to 2 tiles each, six
  // in three columns up to 1.45 tiles, bigger banks reflow from 1.15 tiles.
  // The fit shrinks them (to 0.85) before anything would scroll.
  const n = choices.length;
  const fit = fitTiles({
    count: n,
    w: w ? w - 8 : 0,
    h: h && head.h ? h - head.h - 18 : 0,
    tile,
    max: n <= 4 ? 2 : n <= 6 ? 1.45 : 1.15,
    min: 0.85,
    gap: 0.14,
    maxCols: n <= 4 ? 2 : 3,
  });
  const size = fit.scale;
  return (
    <div ref={rootRef} style={ROOT_STYLE}>
      <ConfettiBurst active={finalRound} originY={40} count={36} reduceMotion={reduceMotion} />
      <Counter done={ri + (solved ? 1 : 0)} total={rounds} accent={accent} />
      {/* Find card: letter badge, "Find" eyebrow, the word big, say-again button. */}
      <div ref={headRef} style={frost({ padding: compact ? "8px 10px 8px 10px" : "10px 14px 10px 12px", display: "flex", alignItems: "center", gap: compact ? 10 : 14, flexShrink: 0, margin: `${compact ? 34 : 0}px auto ${compact ? 8 : 12}px`, width: "min(100%, 520px)", boxSizing: "border-box" })}>
        <span
          aria-hidden="true"
          style={{
            width: compact ? 40 : 48, height: compact ? 40 : 48, borderRadius: compact ? 12 : 14, flexShrink: 0,
            background: `linear-gradient(160deg, ${accent} 0%, ${deep} 100%)`, color: "#fff", boxShadow: `0 3px 0 ${deep}`,
            display: "grid", placeItems: "center", fontFamily: LEARN.font, fontWeight: 700, fontSize: compact ? 22 : 26, lineHeight: 1,
          }}
        >
          {String(letter || "").toUpperCase()}
        </span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: compact ? 13 : 15, fontWeight: 700, color: deep, letterSpacing: "0.04em", textTransform: "uppercase" }}>Find</div>
          <AnimatePresence mode="wait">
            <motion.div
              key={target.word}
              initial={reduceMotion ? false : { y: 10, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={reduceMotion ? undefined : { y: -10, opacity: 0 }}
              transition={{ duration: 0.2 }}
              style={{ fontSize: "clamp(26px, calc(var(--tile, 72px) * 0.42), 38px)", fontWeight: 700, color: solved ? accent : LEARN.color.ink, lineHeight: 1.1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", transition: "color 200ms ease" }}
            >
              {target.word}
            </motion.div>
          </AnimatePresence>
        </div>
        <RoundButton ariaLabel={`Say ${target.word} again`} onClick={() => (replay || speak)?.(line)} accent={accent} deep={deep} reduceMotion={reduceMotion} size={compact ? 44 : 50}>
          <IconSpeaker size={compact ? 20 : 22} />
        </RoundButton>
      </div>

      <div style={{ flex: 1, minHeight: 0, overflow: fit.fits ? "hidden" : "auto", display: "flex", alignItems: "center", justifyContent: "center", padding: 4 }}>
        <AnimatePresence mode="wait" initial={false}>
          <motion.div
            key={ri}
            initial={reduceMotion ? false : { opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={reduceMotion ? undefined : { opacity: 0, scale: 0.96 }}
            transition={{ duration: 0.2 }}
            style={{ display: "grid", gridTemplateColumns: `repeat(${fit.cols}, max-content)`, gap: "calc(var(--tile, 72px) * 0.14)", justifyContent: "center", maxWidth: "100%" }}
          >
            {choices.map((it, i) => {
              const key = itemKey(it);
              const isTarget = it.word === target.word;
              return (
                <div key={key} style={{ position: "relative" }}>
                  <StickerTile
                    letter={letter}
                    assetName={it.assetName}
                    word={it.word}
                    size={size}
                    index={i}
                    accent={accent}
                    reduceMotion={reduceMotion}
                    shake={shakes[key] || 0}
                    pulse={pulses[key] || 0}
                    state={solved ? (isTarget ? "correct" : "dim") : "idle"}
                    disabled={solved}
                    onTap={() => tap(it)}
                  />
                  {solved && isTarget && <SparkleBurst trigger={pulses[key] || 0} color={accent} reduceMotion={reduceMotion} distance={size * 44} />}
                </div>
              );
            })}
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
}
